import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AddProducto from './AddProducto';
import EditProductoPage from './EditProductoPage';
import UploadImages from './UploadImages';
import AdminNavBar from '../../components/AdminNavBar';
import PaginationBar from '../../components/PaginationBar';
import './styles.css';
import supabase from '../../utils/supabaseClient';

function EditProducto() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [productos, setProductos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [subfamilias, setSubfamilias] = useState([]);
  const [ivas, setIvas] = useState([]);
  const [colores, setColores] = useState([]);
  const [tallas, setTallas] = useState([]);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  
  // New state for color and talla management
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [newColorId, setNewColorId] = useState('');
  const [newColorCode, setNewColorCode] = useState('#000000');
  const [showTallaInput, setShowTallaInput] = useState(false);
  const [newTallaId, setNewTallaId] = useState('');
  const colorDropdownRef = useRef(null);
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);

  // Add these state variables at the top of your EditProducto component
  const [showColorEditModal, setShowColorEditModal] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [editColorId, setEditColorId] = useState('');
  const [editColorCode, setEditColorCode] = useState('#000000');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(false);
  const productsPerPage = 50; // Show 50 products per page

  // Filter state for products without images
  const [showOnlyNoImages, setShowOnlyNoImages] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error getting user:', error);
      } else {
        setUser(user);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN') {
          setUser(session?.user || null);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Fetch data with debounced search
  useEffect(() => {
    if (user) {
      const timeoutId = setTimeout(() => {
        fetchData(1, search);
        setCurrentPage(1);
      }, 500); // Debounce search by 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [user, search]);

  const fetchData = async (page = 1, searchQuery = '') => {
    setLoading(true);
    try {
      // Calculate range for pagination
      const from = (page - 1) * productsPerPage;
      const to = from + productsPerPage - 1;

      let productQuery = supabase
        .from('producto')
        .select('*', { count: 'exact' })
        .range(from, to)
        .order('id', { ascending: false }); // Order by newest first

      // Apply search filter if provided
      if (searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase().trim();
        
        // Split search terms by spaces and filter out empty strings
        const searchTerms = searchLower.split(/\s+/).filter(term => term.length > 0);
        
        if (searchTerms.length === 1) {
          // Single term search
          productQuery = productQuery.or(`codigo.ilike.%${searchTerms[0]}%,nombre.ilike.%${searchTerms[0]}%,detalle.ilike.%${searchTerms[0]}%`);
        } else {
          // Multiple terms - each term must be found in at least one of the fields
          searchTerms.forEach(term => {
            productQuery = productQuery.or(`codigo.ilike.%${term}%,nombre.ilike.%${term}%,detalle.ilike.%${term}%`);
          });
        }
      }

      const [productos, subfamilias, ivas, colores, tallas] = await Promise.all([
        productQuery,
        supabase.from('subfamilia').select('id, nombre'),
        supabase.from('iva').select('id, porcentaje'),
        supabase.from('color').select('*'),
        supabase.from('talla').select('id')
      ]);

      // Fetch images only for the products on this page
      let productImages = [];
      if (productos.data && productos.data.length > 0) {
        const productIds = productos.data.map(p => p.id);
        const { data: imagesData, error: imagesError } = await supabase
          .from('images')
          .select('producto_id')
          .in('producto_id', productIds);
        
        if (imagesError) {
          console.error('Error fetching product images:', imagesError);
        } else {
          // Create a Set of product IDs that have images for quick lookup
          productImages = [...new Set(imagesData?.map(img => img.producto_id) || [])];
        }
      }

      // Add hasImages property to each product
      const productosWithImageInfo = (productos.data || []).map(product => {
        // Check both string and number versions of the ID to handle type mismatches
        const hasImages = productImages.includes(product.id) || 
                         productImages.includes(String(product.id)) ||
                         productImages.includes(Number(product.id));
        
        return {
          ...product,
          hasImages: hasImages
        };
      });

      setProductos(productosWithImageInfo);
      setTotalProducts(productos.count || 0);
      
      // Only update other data on first load or when not searching
      if (page === 1 && !searchQuery) {
        setSubfamilias(subfamilias.data || []);
        setIvas(ivas.data || []);
        setColores(colores.data || []);
        setTallas(tallas.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };  // Function to add a new color
  async function handleAddColor(e) {
    e.preventDefault();
    
    // Validate color inputs
    if (!newColorId.trim()) {
      setMessage('El nombre del color no puede estar vacío');
      return;
    }
    
    // Check if color name already exists
    const colorExists = colores.some(color => color.name === newColorId.trim());
    if (colorExists) {
      setMessage('Este nombre de color ya existe');
      return;
    }
    
    try {
      // Insert new color into the database
      const { data, error } = await supabase
        .from('color')
        .insert([{ 
          name: newColorId.trim(), 
          rgb_code: newColorCode 
        }])
        .select('numeric_id, name, rgb_code');
      
      if (error) throw error;
      
      // Update local state with returned data (including the generated numeric_id)
      const newColor = data[0];
      setColores([...colores, newColor]);
      
      // Reset color picker state
      setNewColorId('');
      setNewColorCode('#000000');
      setShowColorPicker(false);
      setMessage('Color agregado correctamente');
      
    } catch (error) {
      console.error('Error adding color:', error);
      setMessage(`Error al agregar el color: ${error.message}`);
    }
  }

  // Function to add a new talla
  async function handleAddTalla(e) {
    e.preventDefault();
    
    // Validate talla input
    if (!newTallaId.trim()) {
      setMessage('El ID de la talla no puede estar vacío');
      return;
    }
    
    // Check if talla ID already exists
    const tallaExists = tallas.some(talla => talla.id === newTallaId.trim());
    if (tallaExists) {
      setMessage('Esta talla ya existe');
      return;
    }
    
    try {
      // Insert new talla into the database
      const { data, error } = await supabase
        .from('talla')
        .insert([{ id: newTallaId.trim() }]);
      
      if (error) throw error;
      
      // Update local state
      const newTalla = { id: newTallaId.trim() };
      setTallas([...tallas, newTalla]);
      
      // Reset talla input state
      setNewTallaId('');
      setShowTallaInput(false);
      setMessage('Talla agregada correctamente');
      
    } catch (error) {
      console.error('Error adding talla:', error);
      setMessage(`Error al agregar la talla: ${error.message}`);
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target)) {
        setColorDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Remove the old filteredProductos logic since we're now filtering on the server
  // const filteredProductos = useMemo(() => { ... });

  // Products are already filtered by the server-side search
  const sortedProductos = useMemo(() => {
    let filtered = [...productos];
    
    // Apply no-images filter if enabled
    if (showOnlyNoImages) {
      filtered = filtered.filter(prod => !prod.hasImages);
    }
    
    return filtered.sort((a, b) => a.codigo.localeCompare(b.codigo));
  }, [productos, showOnlyNoImages]);

  // Handlers
  const handleEdit = (prod) => {
    setEditing(prod.id); // Set the ID of the product being edited
    setShowEditPopup(true); // Show the edit popup
  };

  // Function to start editing a color
  const handleEditColor = (color) => {
    setEditingColor(color);
    setEditColorId(color.name);
    setEditColorCode(color.rgb_code);
    setShowColorEditModal(true);
  };

  // Function to save edited color
  async function handleSaveEditedColor(e) {
    e.preventDefault();
    
    // Validate color inputs
    if (!editColorId.trim()) {
      setMessage('El nombre del color no puede estar vacío');
      return;
    }
    
    // Check if new color name already exists (only if name has changed)
    if (editColorId !== editingColor.name) {
      const colorExists = colores.some(color => 
        color.name === editColorId.trim() && color.numeric_id !== editingColor.numeric_id
      );
      if (colorExists) {
        setMessage('Este nombre de color ya existe');
        return;
      }
    }
    
    try {
      // Update color in the database by numeric_id
      const { data, error } = await supabase
        .from('color')
        .update({ 
          name: editColorId.trim(), 
          rgb_code: editColorCode 
        })
        .eq('numeric_id', editingColor.numeric_id)
        .select();
      
      if (error) throw error;
      
      // Update local state
      const updatedColors = colores.map(color => {
        if (color.numeric_id === editingColor.numeric_id) {
          return { ...color, name: editColorId.trim(), rgb_code: editColorCode };
        }
        return color;
      });
      
      setColores(updatedColors);
      
      // Reset color editor state
      setEditingColor(null);
      setEditColorId('');
      setEditColorCode('#000000');
      setShowColorEditModal(false);
      setMessage('Color actualizado correctamente');
      
      // Refresh data to update any products using this color
      fetchData(currentPage, search);
      
    } catch (error) {
      console.error('Error updating color:', error);
      setMessage(`Error al actualizar el color: ${error.message}`);
    }
  }

  // Update the handleDelete function to delete inventory records first
  const handleDelete = async (prod) => {
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para eliminar productos');
      return;
    }
    
    if (window.confirm(`¿Estás seguro de que deseas eliminar el producto "${prod.nombre}"?`)) {
      try {
        setMessage(''); // Clear any previous messages
        
        // Step 1: Delete inventory records first
        const { error: inventoryError } = await supabase
          .from('inventario')
          .delete()
          .eq('producto_id', prod.id);
          
        if (inventoryError) {
          console.error('Error deleting inventory records:', inventoryError);
          setMessage('Error al eliminar registros de inventario.');
          return;
        }

        // Step 2: Delete image relationships from the images table
        const { error: imagesError } = await supabase
          .from('images')
          .delete()
          .eq('producto_id', prod.id);
          
        if (imagesError) {
          console.error('Error deleting image records:', imagesError);
          // We can continue even if image deletion fails
        }

        // Step 3: Finally delete the product
        const { error: productError } = await supabase
          .from('producto')
          .delete()
          .eq('id', prod.id);
          
        if (productError) {
          console.error('Error deleting product:', productError);
          setMessage('Error al eliminar el producto.');
          return;
        }

        setMessage('¡Producto eliminado correctamente!');
        fetchData(currentPage, search); // Refresh current page after successful deletion
      } catch (error) {
        console.error('Error during product deletion:', error);
        setMessage(`Error: ${error.message}`);
      }
    }
  };

  const handleShowUploadPopup = () => {
    setShowUploadPopup(true);
  };
  
  // Add a function to handle after upload success
  const handleUploadComplete = () => {
    // Call fetchData to refresh the products list instead
    fetchData(currentPage, search);
    setShowUploadPopup(false);
  };
  
  const [showColorManagementModal, setShowColorManagementModal] = useState(false);

  if (authLoading) {
    return <div className="loading">Cargando...</div>;
  }

  // If user is not authenticated, show login message
  if (!user) {
    return (
      <>
        <AdminNavBar user={user} />
        <div className="page-container">
          <div className="auth-message">
            <h2>Acceso Restringido</h2>
            <p>Debes iniciar sesión para acceder a la gestión de productos.</p>
            <button 
              onClick={() => navigate('/admin')}
              className="primary-button"
            >
              Ir a Iniciar Sesión
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavBar user={user} />
      <div className="page-container">
        {/* Add message display near the top */}
        {message && (
          <div className={`message ${message.includes('Error') ? 'error-message' : 'success-message'}`} 
               style={{ marginBottom: '20px', padding: '10px', borderRadius: '4px' }}>
            {message}
          </div>
        )}

        <div className="page-header">
          <h2>Editar Producto</h2>
          <div className="header-actions">
            {user && (
              <span className="user-info">
                Conectado como: {user.email}
              </span>
            )}
            <button 
              className="secondary-button" 
              onClick={() => setShowColorManagementModal(true)}
              style={{ marginRight: '10px' }}
            >
              Gestionar Colores
            </button>
            <button 
              className="secondary-button" 
              onClick={handleShowUploadPopup}
              style={{ marginRight: '10px' }}
            >
              Subir Imágenes
            </button>
            <button className="primary-button" onClick={() => setShowAddPopup(true)}>
              Agregar Producto
            </button>
          </div>
        </div>

        {/* AddProducto Popup */}
        {showAddPopup && (
          <div className="popup">
            <div className="popup-content">
              <button className="popup-close" onClick={() => setShowAddPopup(false)} title="Cerrar">
                ×
              </button>
              <AddProducto onClose={() => setShowAddPopup(false)} user={user} />
            </div>
          </div>
        )}

        {/* EditProducto Popup with added color and talla props */}
        {editing && showEditPopup && (
          <div className="popup">
            <div className="popup-overlay" onClick={() => {
              setShowEditPopup(false);
              setEditing(null);
            }}></div>
            <EditProductoPage
              product={productos.find(prod => prod.id === editing)}
              subfamilias={subfamilias}
              ivas={ivas}
              colores={colores}
              tallas={tallas}
              onClose={() => {
                setShowEditPopup(false);
                setEditing(null);
              }}
              onAddColor={handleAddColor}
              onAddTalla={handleAddTalla}
              // Pass these functions to the EditProductoPage
              setColores={setColores}
              setTallas={setTallas}
              user={user}
            />
          </div>
        )}

        {/* Color Picker Modal */}
        {showColorPicker && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: 20,
              borderRadius: 8,
              width: '90%',
              maxWidth: 400,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>Agregar Nuevo Color</h3>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Identificador del Color:
                <input
                  type="text"
                  value={newColorId}
                  onChange={e => setNewColorId(e.target.value)}
                  placeholder="Ej: ROJO, AZUL, VERDE"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    marginTop: 4
                  }}
                />
              </label>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Código RGB:
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    type="color"
                    value={newColorCode}
                    onChange={e => setNewColorCode(e.target.value)}
                    style={{
                      width: 40,
                      height: 40,
                      padding: 0,
                      border: 'none'
                    }}
                  />
                  <input
                    type="text"
                    value={newColorCode}
                    onChange={e => setNewColorCode(e.target.value)}
                    style={{
                      flex: 1,
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #ccc'
                    }}
                  />
                </div>
              </label>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowColorPicker(false);
                    setNewColorId('');
                    setNewColorCode('#000000');
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAddColor}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e60026',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                >
                  Agregar Color
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Talla Input Modal */}
        {showTallaInput && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: 20,
              borderRadius: 8,
              width: '90%',
              maxWidth: 400,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>Agregar Nueva Talla</h3>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Identificador de la Talla:
                <input
                  type="text"
                  value={newTallaId}
                  onChange={e => setNewTallaId(e.target.value)}
                  placeholder="Ej: S, M, L, XL, 36, 38"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    marginTop: 4
                  }}
                />
              </label>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowTallaInput(false);
                    setNewTallaId('');
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAddTalla}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e60026',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                >
                  Agregar Talla
                </button>
              </div>
            </div>
          </div>
        )}

        {/* UploadImages Popup */}
        {showUploadPopup && (
          <div className="popup">
            <div className="popup-overlay" onClick={() => setShowUploadPopup(false)}></div>
            <div 
              className="popup-content upload-popup-content"
              onClick={(e) => e.stopPropagation()} // Stop event propagation here
            >
              <button 
                className="popup-close" 
                onClick={() => setShowUploadPopup(false)}
                title="Cerrar"
              >
                ×
              </button>
              <UploadImages 
                isPopup={true} 
                onClose={() => setShowUploadPopup(false)}
                onUploadComplete={handleUploadComplete}
              />
            </div>
          </div>
        )}

        {/* Search bar and filters */}
        <div className='search-container'>
          <div className="search-and-filters">
            <input
              type="text"
              placeholder="Buscar por nombre o familia..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
            />
            <div className="filter-controls">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={showOnlyNoImages}
                  onChange={e => setShowOnlyNoImages(e.target.checked)}
                />
                <span>Solo productos sin imágenes</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Table */}
        <table className="table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Detalle</th>
              <th>Subfamilia</th>
              <th>Precio 1</th>
              <th>Precio 2</th>
              <th>Precio 3</th>
              <th>Descuento</th>
              <th>IVA (%)</th>
              <th>Color</th>
              <th>Talla</th>
              <th>Imágenes</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="13" style={{ textAlign: 'center', padding: '20px' }}>
                  <div>Cargando productos...</div>
                </td>
              </tr>
            ) : sortedProductos.length === 0 ? (
              <tr>
                <td colSpan="13" style={{ textAlign: 'center', padding: '20px' }}>
                  <div>No se encontraron productos</div>
                </td>
              </tr>
            ) : (
              sortedProductos.map(prod => (
                <tr key={prod.id} className={!prod.hasImages ? 'no-images-row' : ''}>
                  <td>{prod.codigo}</td>
                  <td>{prod.nombre}</td>
                  <td>{prod.detalle}</td>
                  <td>{subfamilias.find(s => s.id === prod.sub_familia_id)?.nombre || ''}</td>
                  <td>{prod.precio1}</td>
                  <td>{prod.precio2}</td>
                  <td>{prod.precio3}</td>
                  <td>{prod.descuento}</td>
                  <td>{ivas.find(i => i.id === prod.iva_id)?.porcentaje * 100 || ''}%</td>
                  <td>{colores.find(c => c.numeric_id === prod.color_numeric_id)?.name || ''}</td>
                  <td>{tallas.find(t => t.id === prod.talla_id)?.id || ''}</td>
                  <td>
                    {prod.hasImages ? (
                      <span className="image-status has-images">
                        ✓ Con imágenes
                      </span>
                    ) : (
                      <span className="image-status no-images">
                        ⚠ Sin imágenes
                      </span>
                    )}
                  </td>
                  <td>
                    <button className="secondary-button" onClick={() => handleEdit(prod)}>
                      Editar
                    </button>
                    <button className="danger-button" onClick={() => handleDelete(prod)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <PaginationBar
          currentPage={currentPage}
          totalItems={totalProducts}
          itemsPerPage={productsPerPage}
          onPageChange={page => {
            setCurrentPage(page);
            fetchData(page, search);
          }}
          label="productos"
        />

        {/* Color Management Modal - Make sure this conditional is correct */}
        {showColorManagementModal && (
          <div className="popup">
            <div className="popup-overlay" onClick={() => setShowColorManagementModal(false)}></div>
            <div className="popup-content color-management-popup">
              <button 
                className="popup-close" 
                onClick={() => setShowColorManagementModal(false)}
                title="Cerrar"
              >
                ×
              </button>
              <h2>Gestión de Colores</h2>
              
              <div className="color-management-actions">
                <button 
                  className="primary-button" 
                  onClick={() => {
                    setShowColorPicker(true);
                    setShowColorManagementModal(false);
                  }}
                >
                  Agregar Nuevo Color
                </button>
              </div>
              
              <div className="color-list">
                {colores.map(color => (
                  <div key={color.numeric_id} className="color-item">
                    <div 
                      className={`color-preview ${color.name.toLowerCase() === 'colores' ? 'rainbow-gradient' : ''}`}
                      style={color.name.toLowerCase() === 'colores' ? {} : { backgroundColor: color.rgb_code }}
                    ></div>
                    <div className="color-info">
                      <div className="color-name">{color.name}</div>
                      <div className="color-code">{color.rgb_code}</div>
                    </div>
                    <div className="color-actions">
                      <button 
                        className="edit-button" 
                        onClick={() => handleEditColor(color)}
                      >
                        Editar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Color Edit Modal */}
        {showColorEditModal && editingColor && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000  // Higher than the main popup (2000) to appear on top
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: 20,
              borderRadius: 8,
              width: '90%',
              maxWidth: 400,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>Editar Color</h3>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Identificador del Color:
                <input
                  type="text"
                  value={editColorId}
                  onChange={e => setEditColorId(e.target.value)}
                  placeholder="Ej: ROJO, AZUL, VERDE"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    marginTop: 4
                  }}
                />
              </label>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Código RGB:
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    type="color"
                    value={editColorCode}
                    onChange={e => setEditColorCode(e.target.value)}
                    style={{
                      width: 40,
                      height: 40,
                      padding: 0,
                      border: 'none'
                    }}
                  />
                  <input
                    type="text"
                    value={editColorCode}
                    onChange={e => setEditColorCode(e.target.value)}
                    style={{
                      flex: 1,
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #ccc'
                    }}
                  />
                </div>
              </label>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowColorEditModal(false);
                    setEditingColor(null);
                    setEditColorId('');
                    setEditColorCode('#000000');
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveEditedColor}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e60026',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add some CSS for the new elements */}
        <style jsx="true">{`
          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .header-actions {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .user-info {
            font-size: 14px;
            color: #666;
            margin-right: 15px;
          }
          .header-buttons {
            display: flex;
            align-items: center;
          }
          .upload-container {
            display: flex;
            align-items: center;
            margin-right: 10px;
          }
          .upload-button {
            cursor: pointer;
          }
          .spinner {
            display: inline-block;
            width: 20px;
            height: 20px;
            margin-left: 10px;
            border: 2px solid rgba(0, 0, 0, 0.1);
            border-left-color: #09f;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          .upload-message {
            margin-left: 10px;
            font-size: 14px;
            color: #555;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .upload-popup-content {
            width: 90%;
            max-width: 1000px;
            max-height: 85vh;
            overflow-y: auto;
          }
          .color-management-popup {
            width: 90%;
            max-width: 800px;
            max-height: 80vh;
            overflow-y: auto;
          }
          .color-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px;
            border-bottom: 1px solid #ddd;
          }
          .color-preview {
            width: 40px;
            height: 40px;
            border-radius: 4px;
            border: 1px solid #ccc;
          }
          .color-info {
            flex: 1;
            margin-left: 10px;
          }
          .color-name {
            font-weight: 500;
          }
          .color-code {
            font-size: 14px;
            color: #777;
          }
          .color-actions {
            display: flex;
            align-items: center;
          }
          .edit-button {
            background-color: #007bff;
            color: white;
            border: none;
            border-radius: 4px;
            padding: 6px 12px;
            cursor: pointer;
            font-size: 14px;
          }
          .edit-button:hover {
            background-color: #0056b3;
          }
          /* Image status styles */
          .image-status {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            text-align: center;
            display: inline-block;
            min-width: 80px;
          }
          .image-status.has-images {
            background-color: #d4edda;
            color: #155724;
            border: 1px solid #c3e6cb;
          }
          .image-status.no-images {
            background-color: #fff3cd;
            color: #856404;
            border: 1px solid #ffeaa7;
          }
          /* Row highlighting for products without images */
          .no-images-row {
            background-color: #fffbf0;
            border-left: 4px solid #f39c12;
          }
          .no-images-row:hover {
            background-color: #fef7e6;
          }
          /* Table adjustments */
          .table th, .table td {
            padding: 10px 8px;
            font-size: 14px;
          }
          .table th:nth-child(12), .table td:nth-child(12) {
            text-align: center;
            width: 120px;
          }
          /* Search and filter styles */
          .search-and-filters {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 20px;
          }
          .search-input {
            flex: 1;
            max-width: 400px;
          }
          .filter-controls {
            display: flex;
            align-items: center;
            gap: 15px;
          }
          .filter-checkbox {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            font-size: 14px;
            color: #333;
          }
          .filter-checkbox input[type="checkbox"] {
            margin: 0;
            cursor: pointer;
          }
          .filter-checkbox:hover {
            color: #e60026;
          }
        `}</style>
      </div>
    </>
  );
}

export default EditProducto;